/* eslint-disable max-len */
import { BaseDateValue, DateValue } from './values';

/**
 * logic-parser overview
 * There is two step, first is to verify the logic is correct and the second is to parse into JS
 * The parser only convert our syntax to JS syntax along with context
 * The first part (validator) is based on a very long regex,
 * it is only there for security reason to not allow any JS code other than our syntax.
 */

const whitespace = '\\s*',
  num = '-?\\d+(?:\\.\\d+)?',
  bool = '(?:true|false)',
  strWithDoubleQuotes = '"[^"]*"',
  strWithSingleQuotes = "'[^']*'",
  date = '\\d{4}-(?:0?[1-9]|1[0-2])-(?:0?[1-9]|[1-2][0-9]|3[0-1])',
  time = '(?:[0-1]?\\d|2[0-4]):[0-5]?\\d',
  datetime = `${date} ${time}`,
  // 1 group
  dateFunction = `date\\(${whitespace}(${datetime}|${date})${whitespace}\\)`,
  // 1 group
  timeFunction = `date\\(${whitespace}(${time})${whitespace}\\)`,
  todayVariable = 'today',
  // 2 groups
  variable = '\\[([^\\](]+)(?:\\((\\d+)\\))?]',
  datediffFunction = `datediff\\(${whitespace}(${dateFunction}|${todayVariable}|${variable})${whitespace},${whitespace}(${dateFunction}|${todayVariable}|${variable})${whitespace},${whitespace}(year|month|day)${whitespace}\\)`,
  strCombined = `${strWithDoubleQuotes}|${strWithSingleQuotes}`,
  strWithVariableArg = `(?:${strCombined}|${variable})`,
  strWithFunctions = `(?:lowercase|uppercase)\\(${whitespace}${strWithVariableArg}${whitespace}\\)`,
  containsFunction = `contains\\(${whitespace}(?:${strWithVariableArg}|${strWithFunctions})${whitespace},${whitespace}(?:${strCombined})${whitespace}\\)`,
  arrayFunction = `(?:includesAll|includesAny|excludesAny|excludesAll)\\(${whitespace}${variable}${whitespace},${whitespace}${strWithVariableArg}(?:${whitespace},${whitespace}${strWithVariableArg})*${whitespace}\\)`,
  vSet = `(?:!*${whitespace}(?:debug\\()?${whitespace}(?:${num}|${bool}|${strWithDoubleQuotes}|${strWithSingleQuotes}|${variable}|${dateFunction}|${timeFunction}|${todayVariable}|${datediffFunction}|${strWithFunctions}|${containsFunction}|${arrayFunction})${whitespace}\\)?)`,
  bracketStart = '\\(*',
  bracketEnd = '\\)*',
  operators = '(?:[+\\-\\/*])',
  comparators = '(?:[=<>]|[!<>]=)',
  combinators = '(?:and|or)';

export function validateLogic(logic: string) {
  if (!balanced(logic, '(', ')') || !balanced(logic, '[', ']')) {
    return false;
  }

  let lhs: string, rhs: string, operatorsSet: string;

  // prettier-ignore
  const regexString = '^' +
  '(?:' +

    (lhs =
      '(?:' +
        '(?:' +
          whitespace +
          bracketStart +
          whitespace +
          vSet +
        ')' +
        (operatorsSet =
          '(?:' +
            whitespace +
            operators +
            whitespace +
            bracketStart +
            whitespace +
            vSet +
            whitespace +
            bracketEnd +
          ')*'
        ) +
      ')'
    ) +

    (rhs =
      '(?:' +
        '(?:' +
          whitespace +
          comparators +
          whitespace +
          bracketStart +
          whitespace +
          vSet +
          whitespace +
          bracketEnd +
        ')' +
        operatorsSet +
      ')'
    ) +

    // combination
    '(?:' +
      whitespace +
      combinators +

      lhs +

      rhs +

    ')*' +
  ')*' +

  whitespace +
  '$';

  const regex = new RegExp(regexString, 'i');

  return regex.test(logic);
}

function balanced(input: string, start: string, end: string) {
  let bracketCount = -1;
  for (const char of input) {
    if (char === start) {
      bracketCount++;
    } else if (char === end) {
      if (bracketCount === -1) {
        return false;
      } else {
        bracketCount--;
      }
    }
  }
  return bracketCount === -1;
}

function extractUnsafeGroups(logic: string) {
  const values: string[] = [];
  let safeLogic = logic;

  const regex = /"[^"]*"|'[^']*'|\[[^\]]*\]/g;
  let result: RegExpExecArray | null = regex.exec(logic);
  while (result !== null) {
    result.forEach(match => {
      const length = values.push(match);
      safeLogic = safeLogic.replace(match, `$${length - 1} `);
    });
    result = regex.exec(logic);
  }
  return { safeLogic, values };
}

function applyGroups(safeLogic: string, values: string[]) {
  let logic = safeLogic;

  values.forEach((str, index) => {
    logic = logic.replace(`$${index}`, str);
  });

  return logic;
}

export function execLogic(logic: string, context: Record<string, unknown>): boolean {
  const extra = {};
  Object.entries(context).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      const arr = val;
      extra[key] = arr.map(obj => context[Object.keys(obj)[0]]);
      arr.forEach(obj => {
        extra[key + '_' + Object.keys(obj)[0]] = Object.values(obj)[0];
      });
    }
  });
  const script = parseLogic(logic, { ...context, ...extra });
  // eslint-disable-next-line no-eval
  return !!eval(script);
}

/**
 * convert the logic syntax into js syntax
 *
 * @returns string   executable jscode
 */
function parseLogic(logic: string, context: object) {
  if (!validateLogic(logic)) {
    throw new Error(`Invalid logic: ${logic}`);
  }

  logic = logic
    // get ref, pick name and index
    .replace(new RegExp(variable, 'gi'), (_, key, index) => {
      let value = context[key] || '';
      if (String(key).indexOf('option_') === 0 && value === '') {
        value = undefined;
      }
      /* istanbul ignore if reason: not being used */
      if (index != null) {
        if (Array.isArray(value)) value = value[index];
        else value = '';
      } else if (value instanceof BaseDateValue) {
        return `date(${value.format()})`;
      }
      return JSON.stringify(value);
    });

  const { safeLogic, values } = extractUnsafeGroups(logic);
  logic = safeLogic;

  // replace = to == without !,<,>
  logic = logic.replace(/([^!|<|>])=/g, '$1==');
  // replace and with &&
  logic = logic.replace(/\band\b/gi, '&&');
  // replace or with ||
  logic = logic.replace(/\bor\b/gi, '||');
  // replace true (case-insensitive) with true
  logic = logic.replace(/\btrue\b/gi, 'true');
  // replace false (case-insensitive) with false
  logic = logic.replace(/\bfalse\b/gi, 'false');
  // replace today with today date
  logic = logic.replace(/\btoday\b/gi, `date(${new DateValue(new Date().toJSON()).format()})`);
  // replace datediff with datediff function
  logic = logic.replace(new RegExp(datediffFunction, 'gi'), 'datediff($1, $5, "$9")'); // skipping group number as date also has groups
  // replace date with date and time function
  logic = logic.replace(new RegExp(dateFunction, 'gi'), 'date("$1")');
  logic = logic.replace(new RegExp(timeFunction, 'gi'), 'time("$1")');

  logic = applyGroups(logic, values);

  // replace empty value to NaN when comparing
  logic = logic.replace(/(''|"")(\s*(?:\<|\>)=?)/g, 'NaN$2');
  logic = logic.replace(/((?:\<|\>)=?\s*)(''|"")/g, '$1NaN');

  return `
(function () {
    return ${logic};

    function date(str) {
      // str could be 2020-4-12 or 2020-04-12
      // split the date components into year, month, and date
      var datetime = str.split(' ');
      var dates = datetime[0].split('-');
      if (datetime.length == 1) {
        return new Date(Date.UTC(parseInt(dates[0]), parseInt(dates[1])-1, parseInt(dates[2]))).valueOf();
      }
      var times = datetime[1].split(':');
      return new Date(Date.UTC(parseInt(dates[0]), parseInt(dates[1])-1, parseInt(dates[2]), parseInt(times[0]), parseInt(times[1]))).valueOf();
    }
    function time(str) {
      // split the time components into hour, minute
      var times = str.split(':');
      return new Date(Date.UTC(1970, 0, 1, parseInt(times[0]), parseInt(times[1]))).valueOf();
    }
    function datediff(date1, date2, unit){
      switch(unit.toLowerCase()) {
        case 'year':
          var year = new Date(date2 - date1).getFullYear() - 1970;
          return year < 0 ? year + 1 : year;
        case 'month':
          var month = new Date(date2 - date1).getMonth() + datediff(date1, date2, 'year') * 12;
          return date1 > date2 ? month - 11 : month;
        case 'day':
          return Math.ceil((date2 - date1) / 1000 / 60 / 60 / 24);
      }
    }
    function lowercase(str) {
      return typeof str === 'string' ? str.toLowerCase() : str;
    }
    function uppercase(str) {
      return typeof str === 'string' ? str.toUpperCase() : str;
    }
    function contains(str, substr) {
      return typeof str === 'string' ? str.includes(substr) : str;
    }
    function includesAll(items) {
      var valArray = Array.prototype.slice.call(arguments, 1);
      if (!Array.isArray(items)) {
        items = [items];
      }
      var result = true;
      
      for (var index = 0; index < valArray.length; index++) {
        var val = valArray[index];
        result = items.indexOf(val) !== -1;
        if (!result) {
          break;
        }
      }

      return result;
    }
    function includesAny(items) {
      var valArray = Array.prototype.slice.call(arguments, 1);
      if (!Array.isArray(items)) {
        items = [items];
      }
      
      for (var index = 0; index < valArray.length; index++) {
        var val = valArray[index];
        if (items.indexOf(val) !== -1) {
          return true
        }
      }

      return false;
    }
    function excludesAny(items) {
      var valArray = Array.prototype.slice.call(arguments, 1);
      if (!Array.isArray(items)) {
        items = [items];
      }
      
      for (var index = 0; index < valArray.length; index++) {
        var val = valArray[index];
        if (items.indexOf(val) === -1) {
          return true
        }
      }

      return false;
    }
    function excludesAll(items) {
      var valArray = Array.prototype.slice.call(arguments, 1);
      if (!Array.isArray(items)) {
        items = [items];
      }

      for (var index = 0; index < valArray.length; index++) {
        var val = valArray[index];
        if (items.indexOf(val) !== -1) {
          return false;
        }
      }

      return true;
    }
    function debug(val) {
      console.log(val);
      return val;
    }
})()`;
}
